/**
 * TaskStatusStore
 * 
 * 
 */

import type { TaskStatusModel } from "@/models/TaskStatusModel";

import { defineStore } from "pinia";
import { Ref, ref } from "vue";
import Api from "@/services/Api";
import Cache from "@/services/Cache";
import Events from "@/services/Emitter";

const taskStatusStore = () =>
{
    const CACHE_KEY = "store_taskStatus_data";
    const list = ref<Record<number, TaskStatusModel>>({});
    const isInitialized = ref<boolean>(false);
    let initPromise: Promise<void> | null = null;

    const init = async (): Promise<void> => {
        // If already initialized, return immediately
        if (isInitialized.value) return;
        
        // If initialization is in progress, return the existing promise
        if (initPromise) return initPromise;
        
        // Start new initialization
        initPromise = (async () => {
            try {
                // Check cache first
                const cached = await Cache.get(CACHE_KEY);
                if (cached && typeof cached === 'object' && Object.keys(cached).length > 0) {
                    list.value = cached;
                    isInitialized.value = true;
                    return;
                }
                
                // Load from server if cache is empty
                await reload();
            } finally {
                // Clear the promise when done (success or failure)
                initPromise = null;
            }
        })();
        
        return initPromise;
    };

    const reload = async (runInBackground:boolean = false):Promise<Ref<Record<number, TaskStatusModel>>> =>
    {
        let tmp = await Api.loadTaskStatus(runInBackground);
        let rec = {} as Record<number, TaskStatusModel>;
        for (let i:number = 0; i < tmp.length; i++)
        {
            if (tmp[i].Id !== undefined) {
                rec[tmp[i].Id] = tmp[i];
            }
        }
        list.value = rec;
        await Cache.set(CACHE_KEY, list.value);
        isInitialized.value = true;
        return list;
    }

    const refresh = async (): Promise<void> => {
        await reload();
    };

    const load = async (runInBackground:boolean = false)
        :Promise<Ref<Record<number, TaskStatusModel>>> =>
    {
        await init();
        return list;
    }
    
    const getById = async (status:number):Promise<TaskStatusModel | null> =>
    {
        await init();
        
        if (list.value[status]) {
            return list.value[status];
        }
        
        return null;
    }

    // register global events
    Events.on("TaskStatusStore::Reload", async () =>
    {
        console.debug("TaskStatusStore::Reload called...");
        await reload();
    });

    Events.on("refresh:taskStatusStore", async () => {
        console.debug("refresh:taskStatusStore event received");
        await refresh();
    });

    // Auto-initialize on store creation
    init();

    return { load, reload, refresh, getById, list };
};

export const useTaskStatusStore = defineStore("taskStatus", taskStatusStore);